export type PdfTextItemLike = {
    str?: unknown;
    transform?: unknown;
    width?: unknown;
    height?: unknown;
    hasEOL?: unknown;
};

export type PdfPageLayout = {
    mode: 'empty' | 'lines' | 'two-column';
    lineCount: number;
    columnSplit: number | null;
    needsVision: boolean;
    reasons: string[];
};

type Token = {
    text: string;
    x: number;
    y: number;
    width: number;
    height: number;
};

type Segment = {
    text: string;
    xStart: number;
    xEnd: number;
    y: number;
};

const num = (value: unknown, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

function tokenise(items: PdfTextItemLike[]): Token[] {
    return (Array.isArray(items) ? items : []).flatMap((item) => {
        const text = typeof item?.str === 'string' ? item.str.trim() : '';
        if (!text) return [];
        const transform = Array.isArray(item?.transform) ? item.transform : [];
        const height = Math.max(1, Math.abs(num(transform[3], num(item?.height, 10))));
        return [{
            text,
            x: num(transform[4]),
            y: num(transform[5]),
            width: Math.max(0, num(item?.width)),
            height,
        }];
    });
}

function groupIntoSegments(tokens: Token[], pageWidth: number): Segment[][] {
    const lines: Token[][] = [];
    const sorted = [...tokens].sort((a, b) => b.y - a.y || a.x - b.x);
    for (const token of sorted) {
        const tolerance = Math.max(2, Math.min(7, token.height * 0.45));
        const line = lines.find((candidate) => Math.abs((candidate[0]?.y ?? token.y) - token.y) <= tolerance);
        if (line) line.push(token);
        else lines.push([token]);
    }

    const segmentGap = Math.max(28, pageWidth * 0.065);
    return lines
        .sort((a, b) => (b[0]?.y ?? 0) - (a[0]?.y ?? 0))
        .map((line) => {
            const ordered = [...line].sort((a, b) => a.x - b.x);
            const segments: Segment[] = [];
            for (const token of ordered) {
                const previous = segments[segments.length - 1];
                const estimatedEnd = token.x + Math.max(token.width, token.text.length * token.height * 0.42);
                if (!previous || token.x - previous.xEnd > segmentGap) {
                    segments.push({ text: token.text, xStart: token.x, xEnd: estimatedEnd, y: token.y });
                } else {
                    previous.text = `${previous.text} ${token.text}`.replace(/\s+/g, ' ').trim();
                    previous.xEnd = Math.max(previous.xEnd, estimatedEnd);
                }
            }
            return segments;
        })
        .filter((segments) => segments.length > 0);
}

function detectColumnSplit(lines: Segment[][], pageWidth: number): number | null {
    const minGap = Math.max(72, pageWidth * 0.12);
    const mids: number[] = [];
    for (const line of lines) {
        const ordered = [...line].sort((a, b) => a.xStart - b.xStart);
        for (let index = 1; index < ordered.length; index += 1) {
            const current = ordered[index];
            const previous = ordered[index - 1];
            if (!current || !previous) continue;
            const gap = current.xStart - previous.xEnd;
            const mid = previous.xEnd + gap / 2;
            if (gap >= minGap && mid >= pageWidth * 0.28 && mid <= pageWidth * 0.72) mids.push(mid);
        }
    }
    if (mids.length < 3) return null;
    const tolerance = Math.max(24, pageWidth * 0.05);
    let best: number[] = [];
    for (const midpoint of mids) {
        const cluster = mids.filter((candidate) => Math.abs(candidate - midpoint) <= tolerance);
        if (cluster.length > best.length) best = cluster;
    }
    const minimumSupport = Math.max(3, Math.ceil(lines.length * 0.2));
    if (best.length < minimumSupport) return null;
    return best.reduce((sum, value) => sum + value, 0) / best.length;
}

/**
 * Rebuild readable page text from PDF.js positioned text items. PDF.js item
 * order is not a reading-order guarantee: joining `item.str` flattens columns,
 * tables and title blocks. This deterministic pass groups physical lines and
 * detects a repeated page gutter before a later vision pass handles genuinely
 * complex pages.
 */
export function orderPdfTextItems(
    items: PdfTextItemLike[],
    { width = 595, height = 842 }: { width?: number; height?: number } = {},
): { text: string; layout: PdfPageLayout } {
    const pageWidth = Math.max(1, num(width, 595));
    const pageHeight = Math.max(1, num(height, 842));
    const tokens = tokenise(items);
    if (tokens.length === 0) {
        return {
            text: '',
            layout: { mode: 'empty', lineCount: 0, columnSplit: null, needsVision: true, reasons: ['no-selectable-text'] },
        };
    }

    const lines = groupIntoSegments(tokens, pageWidth);
    const columnSplit = detectColumnSplit(lines, pageWidth);
    const tableLikeLineCount = lines.filter((line) => line.length >= 3).length;
    const reasons: string[] = [];
    if (columnSplit != null) reasons.push('multi-column');
    if (tableLikeLineCount >= 3) reasons.push('table-like');
    if (pageWidth > 1100 || pageHeight > 1100) reasons.push('large-format-page');

    let orderedText: string[];
    if (columnSplit == null) {
        orderedText = lines.map((line) => line.map((segment) => segment.text).join('    '));
    } else {
        const allSegments = lines.flat();
        const left = allSegments.filter((segment) => segment.xEnd <= columnSplit);
        const right = allSegments.filter((segment) => segment.xStart >= columnSplit);
        const spanning = allSegments.filter((segment) => segment.xStart < columnSplit && segment.xEnd > columnSplit);
        const byReadingOrder = (a: Segment, b: Segment) => b.y - a.y || a.xStart - b.xStart;
        const topBodyY = Math.max(...left.map((segment) => segment.y), ...right.map((segment) => segment.y));
        const topSpanning = spanning.filter((segment) => segment.y >= topBodyY).sort(byReadingOrder);
        const remainingSpanning = spanning.filter((segment) => segment.y < topBodyY).sort(byReadingOrder);
        orderedText = [
            ...topSpanning.map((segment) => segment.text),
            ...left.sort(byReadingOrder).map((segment) => segment.text),
            ...right.sort(byReadingOrder).map((segment) => segment.text),
            ...remainingSpanning.map((segment) => segment.text),
        ];
    }

    return {
        text: orderedText.filter(Boolean).join('\n').replace(/\n{3,}/g, '\n\n').trim(),
        layout: {
            mode: columnSplit == null ? 'lines' : 'two-column',
            lineCount: lines.length,
            columnSplit: columnSplit == null ? null : Math.round(columnSplit * 100) / 100,
            needsVision: reasons.length > 0,
            reasons,
        },
    };
}
